import { TableRow, Theme, makeStyles, useTheme } from '@material-ui/core';
import { WbTableCell, WbTooltip } from '@agilelab/plugin-wb-platform';
import { ClassNameMap } from '@material-ui/styles';
import { ReactElement, cloneElement } from 'react';
import { LayoutTemplate, createScaffolderLayout } from '../../../layouts';
import { scaffolderPlugin } from '../../../plugin';
import {
  UI_STYLE_SAFE_KEY,
  UI_TABLE_ROW_TEMPLATE_ORIGINAL_TITLE_KEY,
} from '../../utils';
import { applyDefaultsToTemplateStyle } from './utils';

const useStyles = makeStyles(theme => ({
  tableCell: {
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px ${theme.spacing(
      0.5,
    )}px ${theme.spacing(0.5)}px`,
    '& div:first-child': {
      minHeight: 'auto',
    },
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    overflow: 'hidden',
    isolation: 'isolate',
  },
  firstCell: {
    background: theme.palette.white,
    zIndex: 1,
    position: 'sticky',
    left: 0,
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px ${theme.spacing(
      0.5,
    )}px ${theme.spacing(0.5)}px`,
    '& div:first-child': {
      minHeight: 'auto',
    },
    border: `1px solid ${theme.palette.grey[300]}`,
    borderLeft: 'none',
    borderTop: 'none',
  },
  tableRow: {
    background: theme.palette.white,
    '& .PrivateSwitchBase-root': {
      padding: 0,
    },
  },
}));

function getTableRowCell(
  element: {
    content: ReactElement;
    name: string;
    disabled: boolean;
    readonly: boolean;
    hidden: boolean;
  },
  id: string,
  theme: Theme,
  classes: ClassNameMap<any>,
  index: number,
) {
  const errors: string[] =
    '0' in (element.content.props?.errorSchema ?? {})
      ? element.content.props?.errorSchema?.['0']?.__errors
      : element.content.props?.errorSchema?.__errors;
  const duplicateElement = element.content;

  if (duplicateElement.props.errorSchema?.__errors)
    duplicateElement.props.errorSchema.__errors = errors;

  let uiSchema;

  const rawTemplateStyle = duplicateElement.props.uiSchema[UI_STYLE_SAFE_KEY];
  // Strip width-related properties from the user-provided style:
  // column width is controlled at the table level in ArrayTableTemplate.
  const {
    width: _w,
    minWidth: _min,
    maxWidth: _max,
    ...templateStyle
  } = rawTemplateStyle ?? {};

  const baseTableStyle = {
    borderRadius: theme.spacing(1),
    width: '100%',
    height: '32px',
    minHeight: 'auto',
    'ui:popper': 'wide',
  };

  const field = duplicateElement.props.uiSchema['ui:field'];

  switch (field) {
    case 'CustomUrlPicker':
      uiSchema = {
        ...duplicateElement.props.uiSchema,
        [UI_STYLE_SAFE_KEY]: applyDefaultsToTemplateStyle(templateStyle, {
          ...baseTableStyle,
          paddingTop: '4px',
          paddingBottom: '4px',
          overflowY: 'hidden',
        }),
      };
      break;
    case 'EntitySearchPicker':
      uiSchema = {
        ...duplicateElement.props.uiSchema,
        [UI_STYLE_SAFE_KEY]: applyDefaultsToTemplateStyle(templateStyle, {
          ...baseTableStyle,
          paddingTop: '4px',
          paddingBottom: '4px',
          overflowY: 'hidden',
        }),
      };
      break;
    default:
      uiSchema = {
        ...element.content.props.uiSchema,
        [UI_STYLE_SAFE_KEY]: applyDefaultsToTemplateStyle(
          templateStyle,
          baseTableStyle,
        ),
      };
  }

  const originalTitle = duplicateElement.props.schema.title;

  const cloned = cloneElement(duplicateElement, {
    schema: {
      ...element.content.props.schema,
      // empty title and description since we don't want them to be rendered in the table row
      description: '',
      title: '',
    },
    uiSchema: {
      ...uiSchema,
      // add original title to uiSchema so that it can be used by pickers to show the original title of the field even when rendered inside the table row template
      [UI_TABLE_ROW_TEMPLATE_ORIGINAL_TITLE_KEY]: originalTitle,
      'ui:options': {
        ...(uiSchema['ui:options'] as object),
        inputProps: {
          ...((uiSchema['ui:options']?.inputProps ?? {}) as object),
          'aria-label': originalTitle,
        },
      },
    },
  });
  return (
    <WbTableCell
      colId={id}
      key={id}
      className={index === 0 ? classes.firstCell : classes.tableCell}
    >
      {errors?.length ? (
        <WbTooltip title={errors.join('\n')}>
          <div style={{ width: '100%', height: '100%' }}>{cloned}</div>
        </WbTooltip>
      ) : (
        cloned
      )}
    </WbTableCell>
  );
}

export const TableRowTemplate: LayoutTemplate = (props: any) => {
  const theme = useTheme();
  const classes = useStyles();
  const headerProperties: string[] = (props.schema as any)?.columnHeaders
    ? Array.from((props.schema as any)?.columnHeaders?.keys())
    : [];
  const tableRowCells = headerProperties.map((headerProperty, index) => {
    const cell = props.properties.find(
      (p: any) => p.content.props.name === headerProperty,
    );
    if (cell) {
      return getTableRowCell(cell, headerProperty, theme, classes, index);
    }
    return <WbTableCell key={headerProperty} colId={headerProperty} />;
  });
  return (
    <TableRow className={classes.tableRow}>
      {tableRowCells}
      {(props.schema as any).RowButtons}
    </TableRow>
  );
};

export const TableRowTemplateLayout = scaffolderPlugin.provide(
  createScaffolderLayout({
    name: 'TableRowTemplate',
    component: TableRowTemplate,
  }),
);
